/**
 * <s-welcome-popup> — opens the welcome signup dialog at two moments:
 *
 * Shown at most twice per visitor, ever, and never to subscribers:
 * 1. Once on entry, after `delay` ms (default 4000).
 * 2. Once the first time the cart drawer opens, or on the cart page (`cart-page`).
 *
 * After a signup from this dialog, the reload reopens it to show the success state.
 * A "?welcome=preview" URL always opens it, for checking the design.
 */
const STORAGE_KEY = 'veyilon-welcome-popup';
const SUBMIT_KEY = 'veyilon-welcome-submitted';
const CART_DRAWER_ID = 'cart-drawer';
const CART_DELAY_MS = 1200;

class SWelcomePopup extends HTMLElement {
  #ac;
  #timer;
  #cartTimer;
  #cartObserver;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    const form = this.querySelector('form');
    if (!dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    const justSubmitted = this.#session(SUBMIT_KEY) === '1';
    if (justSubmitted && this.querySelector('[data-welcome-success]')) {
      sessionStorage.removeItem(SUBMIT_KEY);
      this.#store({ state: 'subscribed', at: Date.now() });
      dialog.showModal();
    } else if (this.hasAttribute('cart-page')) {
      if (this.#shouldOpenAtCart()) this.#openAtCart(dialog);
    } else if (this.#shouldOpen()) {
      const delay = Number(this.getAttribute('delay') || 4000);
      this.#timer = setTimeout(() => {
        if (document.querySelector('dialog:modal')) return;
        if (!this.#isPreview()) this.#store({ entryShown: true });
        dialog.showModal();
      }, delay);
    }

    this.#watchCartDrawer(dialog);

    dialog.addEventListener('close', () => {
      const saved = this.#read();
      if (!saved || saved.state !== 'subscribed') this.#store({ state: 'dismissed', at: Date.now() });
    }, opts);

    form?.addEventListener('submit', () => {
      try { sessionStorage.setItem(SUBMIT_KEY, '1'); } catch { /* storage unavailable */ }
    }, opts);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    clearTimeout(this.#cartTimer);
    this.#cartObserver?.disconnect();
    this.#ac?.abort();
  }

  /** Shows the offer once when the cart drawer opens. @param {HTMLDialogElement} dialog */
  #watchCartDrawer(dialog) {
    const drawer = document.getElementById(CART_DRAWER_ID);
    if (!drawer) return;
    this.#cartObserver = new MutationObserver(() => {
      if (!drawer.hasAttribute('open') || dialog.open) return;
      if (this.#shouldOpenAtCart()) this.#openAtCart(dialog);
    });
    this.#cartObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  /** @param {HTMLDialogElement} dialog */
  #openAtCart(dialog) {
    clearTimeout(this.#timer);
    this.#store({ cartShown: true });
    this.#cartTimer = setTimeout(() => {
      if (!dialog.open) dialog.showModal();
    }, CART_DELAY_MS);
  }

  #shouldOpen() {
    if (this.#isPreview()) return true;
    const saved = this.#read();
    if (saved?.state === 'subscribed') return false;
    return !saved?.entryShown;
  }

  #shouldOpenAtCart() {
    if (this.#isPreview()) return false;
    const saved = this.#read();
    if (saved?.state === 'subscribed') return false;
    return !saved?.cartShown;
  }

  #isPreview() {
    if (this.hasAttribute('preview')) return true;
    return new URLSearchParams(window.location.search).get('welcome') === 'preview';
  }

  #read() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
  }

  /** Merges into the saved state so the cart flag and the entry state never erase each other. */
  #store(value) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...(this.#read() || {}), ...value }));
    } catch { /* storage unavailable */ }
  }

  #session(key) {
    try { return sessionStorage.getItem(key); } catch { return null; }
  }
}

if (!customElements.get('s-welcome-popup')) customElements.define('s-welcome-popup', SWelcomePopup);
